import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Popup, type MapLayerMouseEvent } from 'react-map-gl';
import { editorMapRefAtom } from './atoms';
import { EDITED_FILL_LAYER } from './edited-area-layer';
import { REFERENCE_FILL_LAYER } from './reference-areas-layer';
import styles from './area-geometry-editor.module.scss';

interface AreaNameTooltipProps {
  /** The edited area's name. */
  editedName: string;
}

interface Hover {
  name: string;
  longitude: number;
  latitude: number;
}

/**
 * The name of the area under the pointer, for the edited area and every reference.
 * Hidden while a button is held, so it stays out of the way of a vertex drag or a
 * trace, and it never takes the pointer.
 *
 * @param {AreaNameTooltipProps} props - The edited area's name.
 * @return {JSX.Element | null} The tooltip, or nothing off an area.
 */
export function AreaNameTooltip({
  editedName,
}: AreaNameTooltipProps): JSX.Element | null {
  const mapRef = useAtomValue(editorMapRefAtom);
  const [hover, setHover] = useState<Hover | null>(null);

  useEffect(() => {
    if (!mapRef) return undefined;
    /**
     * The first feature of a layer under a point, if the layer is on the map.
     *
     * @param {MapLayerMouseEvent['point']} point - The pointer.
     * @param {string} layer - The layer.
     * @return {object | undefined} The feature.
     */
    const featureAt = (point: MapLayerMouseEvent['point'], layer: string) =>
      mapRef.getLayer(layer)
        ? mapRef.queryRenderedFeatures(point, { layers: [layer] })[0]
        : undefined;
    /**
     * Names the area under the pointer: the edited area wherever it is, even
     * under a reference that loaded on top of it, else the topmost reference.
     *
     * @param {MapLayerMouseEvent} event - The move.
     */
    const handleMove = (event: MapLayerMouseEvent) => {
      if (event.originalEvent.buttons !== 0) {
        setHover(null);
        return;
      }
      const name = featureAt(event.point, EDITED_FILL_LAYER)
        ? editedName
        : (featureAt(event.point, REFERENCE_FILL_LAYER)?.properties?.name as
            string | undefined);
      setHover(
        name
          ? { name, longitude: event.lngLat.lng, latitude: event.lngLat.lat }
          : null
      );
    };
    /** Drops the tooltip: the pointer left, a press began, or the map moved. */
    const clear = () => setHover(null);
    // A press starts a vertex drag, a trace or a pan, none of which fire a
    // mousemove until it ends.
    const clearOn = ['mouseout', 'mousedown', 'movestart'] as const;
    mapRef.on('mousemove', handleMove);
    clearOn.forEach((type) => mapRef.on(type, clear));
    return () => {
      mapRef.off('mousemove', handleMove);
      clearOn.forEach((type) => mapRef.off(type, clear));
    };
  }, [mapRef, editedName]);

  if (!hover) return null;
  return (
    <Popup
      longitude={hover.longitude}
      latitude={hover.latitude}
      anchor="bottom"
      offset={12}
      closeButton={false}
      closeOnClick={false}
      className={styles.areaNameTooltip}
    >
      {hover.name}
    </Popup>
  );
}
